import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { compose } from 'utils/compose';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { FormControlLabel, Checkbox, Container, Grid, CircularProgress } from '@mui/material';

import { Section, EditButton } from 'components/DeliveryManager';
import { DELIVERY_INSTRUCTION, DEFAULT_REQUEST_ERROR_TEXT, COMMERCIAL_BUILDING } from 'constants/common';
import useSearchParams from 'hooks/useSearchParams';
import { updateDelivery } from 'utils/api';
import { get } from 'lodash';
import { BusinessHours } from '../../../components/BusinessHours/BusinessHours';

const PREFIX = 'DeliveryInstruction';

const classes = {
  text: `${PREFIX}-text`,
  italic: `${PREFIX}-italic`,
  formControlLabel: `${PREFIX}-formControlLabel`,
};

const StyledSection = styled(Section)(({ theme }) => ({
  [`& .${classes.text}`]: {
    fontSize: '14px',
    color: theme.colorTokens.onSurfaceTextLabel,
  },

  [`& .${classes.italic}`]: {
    fontStyle: 'italic',
  },

  [`& .${classes.formControlLabel}`]: {
    fontSize: 14,
    color: theme.colorTokens.onSurfaceTextDisplay,
  },
}));

export const DeliveryInstruction = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const { trackingCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { deliveryStore } = store;
  const { delivery, loadingDelivery } = deliveryStore;
  const shipmentID = delivery?.shipment?.id;
  const instruction = delivery?.delivery_instruction;
  const addressType = get(delivery, 'recipient-questionnaire.address_type') || '';
  const haveRoamingPets = Boolean(delivery?.have_free_roaming_pet);
  const isBusiness = get(delivery, 'business');
  const showBusinessHours = get(delivery, 'show_business_hours', false);

  const handleOpenDialog = () => {
    searchParams.delete('dialog');
    searchParams.set('reason', DELIVERY_INSTRUCTION);
    setSearchParams(searchParams);
  };

  const handleChangePetAware = (event) => {
    const haveRoamingPets = event.target.checked;

    updateDelivery(trackingCode, shipmentID, { have_free_roaming_pet: haveRoamingPets })
      .then((response) => {
        if (response.status !== 200) {
          return toast.error(<span className={classes.notice}>{response?.data?.message || DEFAULT_REQUEST_ERROR_TEXT}</span>, {
            hideProgressBar: true,
            theme: 'colored',
          });
        }

        deliveryStore.setDeliveryRoamingPets(haveRoamingPets);
      })
      .catch((error) =>
        toast.error(<span className={classes.notice}>{error?.message || DEFAULT_REQUEST_ERROR_TEXT}</span>, {
          hideProgressBar: true,
          theme: 'colored',
        }),
      );
  };

  if (loadingDelivery) {
    return (
      <Container maxWidth="lg" className={classes.container}>
        <Grid style={{ height: 300 }} container alignItems="center" justifyContent="center">
          <CircularProgress color="primary" />
        </Grid>
      </Container>
    );
  }

  return (
    <StyledSection>
      <div className="section__header">
        <span className="section__title">Delivery Instructions:</span>
        <EditButton size="small" variant="outlined" color="primary" onClick={handleOpenDialog} data-testid="delivery-instruction-edit-button">
          Edit
        </EditButton>
      </div>
      <div className="section__body">
        {instruction ? (
          <p className={clsx(classes.text)}>{instruction}</p>
        ) : (
          <p className={clsx(classes.text, classes.italic)} onClick={handleOpenDialog}>
            Add instruction here...
          </p>
        )}
        <FormControlLabel
          label="I have free roaming pets"
          classes={{ label: classes.formControlLabel }}
          control={<Checkbox size="small" color="primary" checked={haveRoamingPets} onChange={handleChangePetAware} />}
          data-testid="delivery-instruction-pet-aware-checkbox"
        />
        {(addressType === COMMERCIAL_BUILDING || isBusiness) && showBusinessHours && <BusinessHours edit={false} />}
      </div>
    </StyledSection>
  );
});
